import React from 'react';
/**
 * PracticeSession full-page states — Loading, Error, Empty variants.
 *
 * Extracted from PracticeSession.tsx in s69 to reduce its size from 978→~680 lines.
 * All components are pure presentational (no state management).
 */
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { AlertCircle, BookmarkCheck } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { DjayAvatar, DjayToetsAvatar, getDjayThinkingMessage } from '../../mascot';
// ── Loading Spinner (shared animation) ───────────────────────
// F-A11Y-4: respect prefers-reduced-motion. The infinite scale/rotate loop
// is gated on useReducedMotion() so users with motion-sensitivity (or who
// have set the OS-level reduced-motion preference) see a still Djay rather
// than a continuously breathing one.
function LoadingSpinner() {
  const reduce = useReducedMotion();
  return <motion.div animate={reduce ? undefined : {
    scale: [1, 1.05, 1],
    rotate: [0, 3, -3, 0]
  }} transition={reduce ? undefined : {
    duration: 2.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className="mx-auto mb-6" data-id="element-11184" data-mp="k99uq39vnomm">
      {/* @djay-slot: practice-loading */}
      <DjayAvatar size="xl" emotion="thinking" data-id="element-11185" data-mp="1k3huwvrlr8q" />
    </motion.div>;
}
// ── Loading State ────────────────────────────────────────────
export function SessionLoadingState() {
  return <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50" data-id="element-11186" data-mp="3nr1qlsr4wsq3">
      <div className="text-center" data-id="element-11187" data-mp="3soab3zzr98ui">
        <LoadingSpinner data-id="element-11188" data-mp="1lxu0do9zgqpf" />
        <h3 className="text-2xl font-black text-slate-800 mb-3" data-id="element-11189" data-mp="1lxc812qfywms">
          Even geduld...
        </h3>
        <p className="text-slate-500 font-medium text-lg" data-id="element-11190" data-mp="1qvm37dc61x8e">
          {getDjayThinkingMessage().text}
        </p>
      </div>
    </div>;
}
// ── Error State (generation failed) ──────────────────────────
export function SessionErrorState({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11191" data-mp="1fgskuk0qpkvy">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-11192" data-mp="1bg099k4eb3wi">
        <div className="text-6xl mb-6" data-id="element-11193" data-mp="284ocvbfd6ome">😕</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11194" data-mp="236wlciimt4iq">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11195" data-mp="1yfob5k9h9f4i">
          De oefensessie kon niet worden geladen.
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11196" data-mp="1ue31s8w5jiz2">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State (fallback) ───────────────────────────────────
export function SessionEmptyFallback({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11197" data-mp="dmr4vfcmih84">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-50" data-id="element-11198" data-mp="hmrmt9yxlajx">
        <div className="text-6xl mb-6" data-id="element-11199" data-mp="mkt4bqgpl1ta">🎯</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11200" data-mp="rms5ctoz7mn2">
          Geen vragen gevonden
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11201" data-mp="wjiknesyie7f">
          Er zijn momenteel geen vragen beschikbaar voor deze selectie.
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11202" data-mp="15dwx79m3ramu">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State: No Content ──────────────────────────────────
export function SessionEmptyNoContent({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11203" data-mp="wqaw3imbdt9j">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-amber-100 border-2 border-amber-50" data-id="element-11204" data-mp="1bee38vc3fazx">
        <div className="text-6xl mb-6" data-id="element-11205" data-mp="16gvq0sr1m9m4">📭</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11206" data-mp="16hx4z8b6jg94">
          Geen oefenstof beschikbaar
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11207" data-mp="drot1y78ls2z">
          Er is nog geen oefenstof beschikbaar voor dit onderwerp. Probeer een
          ander onderwerp!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11208" data-mp="nojno60e38bz">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty State: All Mastered ────────────────────────────────
export function SessionEmptyAllMastered({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11209" data-mp="2bn415b53m6fx">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-emerald-100 border-2 border-emerald-50" data-id="element-11210" data-mp="1xncfqpc95nrw">
        <div className="text-6xl mb-6" data-id="element-11211" data-mp="21pfw9sds3ik1">🏆</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11212" data-mp="2pe8qnker5icj">
          Alles onder de knie!
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11213" data-mp="2udldi55mipf4">
          Je hebt alle beschikbare vragen voor dit onderwerp al goed beantwoord.
          Geweldig gedaan!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-emerald-200" data-id="element-11214" data-mp="2kmqsf4445vv9">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Safety Error (question undefined but session has questions) ─
export function SessionSafetyError({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-11215" data-mp="3ixyldzqem8m5">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-11216" data-mp="3dyly5dvmezj1">
        <div className="text-6xl mb-6" data-id="element-11217" data-mp="38z90dy37o81o">🤔</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-11218" data-mp="340xrbpkqq1ki">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-11219" data-mp="2z9pfkmvxu446">
          We konden de vraag niet laden. Probeer het nog eens!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-11220" data-mp="2v6jdzsjrt141">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Shared modal scaffolding ─────────────────────────────────
// Both exit modals (practice + assessment) share an identical backdrop +
// spring-in card frame. Extracted to keep the modals themselves focused on
// copy / visuals / call-to-action.
function ExitModalFrame({
  show,
  onDismiss,
  borderTone,
  labelledBy,
  children
}: {
  show: boolean;
  onDismiss: () => void;
  borderTone: 'indigo' | 'amber';
  labelledBy: string;
  children: React.ReactNode;
}) {
  const focusTrapRef = useFocusTrap(show);
  const borderClass = borderTone === 'indigo' ? 'border-indigo-100' : 'border-amber-100';
  return <AnimatePresence data-id="element-11221" data-mp="1sh323qjyeuvl">
      {show && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onDismiss} aria-hidden="true" data-id="element-11222" data-mp="1xf4ke99k2q60" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} ref={focusTrapRef} className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby={labelledBy} data-id="element-11223" data-mp="19nz7a1v02bze">
            <Card className={`max-w-md w-full p-8 shadow-2xl border-2 ${borderClass} bg-white rounded-3xl`} data-id="element-11224" data-mp="3gk61n18w9826">
              {children}
            </Card>
          </motion.div>
        </>}
    </AnimatePresence>;
}
// ── Practice Exit Modal ──────────────────────────────────────
// Used during a free-practice / lab / fluency session. Copy is intentionally
// reassuring: practice answers are persisted per-question via the onAnswer
// callback (mastery, XP, daily streak all update on submit), so stopping
// mid-session does NOT lose work — only the end-of-session summary screen.
// This modal is distinct from the assessment ExitConfirmModal below, which
// uses the stricter "incomplete attempt" framing required by 'toets' mode.
export function PracticeExitModal({
  show,
  answeredCount,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  answeredCount: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <ExitModalFrame show={show} onDismiss={onContinue} borderTone="indigo" labelledBy="practice-exit-title" data-id="element-11225" data-mp="3cig9qeox2578">
      <div className="text-center" data-id="element-11226" data-mp="32ipay0tbp41c">
        <div className="mx-auto mb-4 flex justify-center" data-id="element-11227" data-mp="32jqqoilaaepe">
          <DjayAvatar size="lg" emotion="thinking" animate data-id="element-11228" data-mp="37hshxqsv5kby" />
        </div>
        <h2 id="practice-exit-title" className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-11229" data-mp="37jwlg7muohbp">
          Stoppen met oefenen?
        </h2>
        <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-11230" data-mp="2hpn1e2u0t4if">
          Geen probleem — je antwoorden blijven bewaard. Als je later weer
          oefent, start je een nieuwe sessie.
        </p>
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-6 text-left" data-id="element-11231" data-mp="27r7g4ghocpgu">
          <div className="flex items-start gap-3" data-id="element-11232" data-mp="2vgs2l9d1e4uv">
            <BookmarkCheck className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-11233" data-mp="30f3ahejru5ag" />
            <div className="text-sm text-indigo-900 font-medium leading-relaxed" data-id="element-11234" data-mp="2lqgm6nc62373">
              <p className="font-bold mb-0.5" data-id="element-11235" data-mp="2lodwy5iu8e3f">
                Je antwoorden zijn al opgeslagen
              </p>
              <p className="text-indigo-800" data-id="element-11236" data-mp="3ef4cixbrviri">
                {answeredCount} van de {totalQuestions} vragen tellen mee voor
                je mastery, XP en streak.
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-11237" data-mp="17q3qg39klr67">
          <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-11238" data-mp="17p10bxk2nsrc">
            Doorgaan met oefenen
          </Button>
          <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-slate-700" data-id="element-11239" data-mp="1hgoxmf3b840q">
            Stop voor nu
          </Button>
        </div>
      </div>
    </ExitModalFrame>;
}
// ── Exit Confirmation Modal (assessment / toets mode) ────────
// Stricter framing for graded 'toets' sessions where stopping mid-session
// produces an explicit "incomplete" attempt record. Visual now uses
// DjayToetsAvatar (test-themed Djay) instead of the generic 🤔 emoji so the
// modal matches the rest of the assessment shell's visual language.
export function ExitConfirmModal({
  show,
  currentIndex,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  currentIndex: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <ExitModalFrame show={show} onDismiss={onContinue} borderTone="amber" labelledBy="assessment-exit-title" data-id="element-11240" data-mp="11uk9bmw54zng">
      <div className="text-center" data-id="element-11241" data-mp="16w9j6z7mn79y">
        <div className="mx-auto mb-4 flex justify-center" data-id="element-11242" data-mp="1bt9tjmbiew6v">
          <DjayToetsAvatar domain="toets" size="lg" animate data-id="element-11243" data-mp="1brozryx0rha2" />
        </div>
        <h2 id="assessment-exit-title" className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-11244" data-mp="1fubty6h5xeaf">
          Toets stoppen?
        </h2>
        <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-11245" data-mp="o508wnq18ehb">
          Weet je zeker dat je wilt stoppen? Je voortgang wordt opgeslagen als
          onvolledig.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left" data-id="element-11246" data-mp="wwip2ymzauae">
          <div className="flex items-start gap-3" data-id="element-11247" data-mp="ew5ohgragqu">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-11248" data-mp="ca2eu0wenef" />
            <p className="text-sm text-amber-800 font-medium" data-id="element-11249" data-mp="4eeuhobp7byy">
              Je hebt {currentIndex + 1} van de {totalQuestions} vragen
              beantwoord. Als je stopt, kun je later opnieuw beginnen.
            </p>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-11250" data-mp="3k0wie4t4ic64">
          <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-11251" data-mp="3jztqpunz85p5">
            Doorgaan met toets
          </Button>
          <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-11252" data-mp="3f4615e5l1aj4">
            Stoppen
          </Button>
        </div>
      </div>
    </ExitModalFrame>;
}